import { useEffect, useRef, useState } from 'react';

const MIN_DELTA = 30;
const TOOLBAR_HEIGHT = 136;

const useDrawer = (minHeight: number, initialHeight: number) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(initialHeight);

  const dragPosition = useRef(0);
  const dragDirection = useRef<'up' | 'down' | null>(null);
  const totalDelta = useRef(0);
  // we lock the scroll if the user is scrolling the content
  const lockScroll = useRef<boolean | null>(null);

  const getMaxHeight = () => window.innerHeight - TOOLBAR_HEIGHT;

  useEffect(() => {
    if (!wrapperRef.current) return undefined;

    // If the content becomes smaller, we need to adjust the height
    const observer = new ResizeObserver(() => {
      const maxHeight = getMaxHeight();
      setHeight((prev) => Math.min(maxHeight, prev));
    });

    observer.observe(wrapperRef.current);

    return () => observer.disconnect();
  }, [wrapperRef.current]);

  // recusively check if any parent is scrollable in the given direction
  // until we reach the root element (ref.current)
  const isTargetScrollable = (target: HTMLElement, isUp: boolean): boolean => {
    // if we already started dragging, the interface won't scroll anymore
    if (dragDirection.current) return false;
    if (!target || target === wrapperRef.current) return false;

    const isScrollable = isUp
      ? target.scrollTop > 0
      : target.scrollHeight - target.clientHeight > target.scrollTop;

    if (isScrollable) return true;

    return isTargetScrollable(target.parentElement!, isUp);
  };

  const onTouchStart = (event: React.TouchEvent) => {
    dragPosition.current = event.touches[0].clientY;
  };

  const onTouchMove = (event: React.TouchEvent) => {
    const maxHeight = getMaxHeight();

    const y = event.touches[0].clientY;
    const deltaY = y - dragPosition.current;

    const target = event.target as HTMLElement;
    if (lockScroll.current === null) {
      const shouldLockScroll = isTargetScrollable(target, deltaY > 0);
      lockScroll.current = shouldLockScroll;
    }
    if (lockScroll.current) return;

    totalDelta.current += deltaY;

    const newDirection = deltaY > 0 ? 'down' : 'up';
    dragDirection.current = newDirection;

    dragPosition.current = y;

    setHeight((prev) => {
      return Math.min(maxHeight, Math.max(minHeight, prev - deltaY));
    });
  };

  const onTouchEnd = () => {
    const delta = Math.abs(totalDelta.current);
    totalDelta.current = 0;
    lockScroll.current = null;

    if (!dragDirection.current) return;

    const getNewHeight = () => {
      const maxHeight = getMaxHeight();
      if (delta < MIN_DELTA) {
        const isCloserToUp = maxHeight - height < height - minHeight;
        return isCloserToUp ? maxHeight : minHeight;
      }

      return dragDirection.current === 'down' ? minHeight : maxHeight;
    };

    setHeight(getNewHeight());
    dragDirection.current = null;
  };

  const maxHeight = window.innerHeight - TOOLBAR_HEIGHT;

  return {
    wrapperRef,
    height,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    maxHeight,
    dragDirection: dragDirection.current,
  };
};

export default useDrawer;
